import {
	CommunityKind,
	CommunityDiscoveryListItem,
	CommunityUserInfo
} from "client/Api/Communities/community";
import {
	Fragment,
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./CommunityCard.module.less";
import {
	Button,
	DropOverActionList,
	IconButton
} from "client/Components";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import {
	Action,
	ActionKind,
	reduceToAvailableActions
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { useCallbackWithoutArguments } from "client/Hooks";
import classNames from "classnames";
import { CommunityCoverImage } from "./CommunityCoverImage";
import { MemberStatistic } from "./MemberStatistic";
import { CardStatus } from "client/Features/Workspace/Tiles/Hub/Card/CardStatus/CardStatus";

export type CommunityCardProps = {
	item: CommunityDiscoveryListItem;
	isChangingMembership: boolean;
	onNavigateToFeed: () => void;
	onJoin: () => void;
	onLeave: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onShowMembers: () => void;
};

export const CommunityCard = (props: CommunityCardProps): ReactElement => {
	const {
		item,
		isChangingMembership,
		onNavigateToFeed: onNavigateToFeedFromProps,
		onJoin,
		onLeave,
		onEdit,
		onDelete,
		onShowMembers
	} = props;

	const onNavigateToFeed: typeof onNavigateToFeedFromProps = useCallbackWithoutArguments(onNavigateToFeedFromProps);

	const {
		canAccess,
		isNew
	} = item.userInfo;

	const isMember: boolean = item.kind === CommunityKind.AllCompany
		|| item.userInfo.isMember;

	return (
		<div
			{...setTrackingId("community", `|${item.id}`)}
			className={classNames(
				styles.root,
				{ [styles.root_clickable]: canAccess }
			)}
			onClick={
				canAccess
					? onNavigateToFeed
					: undefined
			}
		>
			<div className={styles.cover}>
				<div className={styles.coverScaleContainer}>
					<CommunityCoverImage
						image={item.image}
					/>
				</div>
				{isNew && (
					<div className={styles.newStatus}>
						<CardStatus trackingId="status">
							{localize("_New")}
						</CardStatus>
					</div>
				)}
			</div>

			<div className={styles.infoSection}>
				<div className={styles.header}>
					<h3
						{...setTrackingId("name")}
						className={styles.title}
						title={item.name}
					>
						{item.name}
					</h3>

					<MoreOptions
						trackingId={generateTrackingId("community-action", `|${item.id}`)}
						name={item.name}
						userInfo={item.userInfo}
						onEdit={onEdit}
						onDelete={onDelete}
					/>
				</div>

				<p
					{...setTrackingId("description")}
					className={styles.description}
					title={item.description ?? undefined}
				>
					{item.description}
				</p>

				<div>
					<MemberStatistic
						membersCount={item.membersCount}
						onClick={canAccess ? onShowMembers : undefined}
					/>
				</div>

				<div>
					{isMember
						? (
							<Button
								kind="secondary"
								icon="check"
								text={
									isChangingMembership
										? localize("_Leaving") + "…"
										: localize("_Joined")
								}
								isDisabled={item.kind === CommunityKind.AllCompany || isChangingMembership}
								onClick={onLeave}
								trackingId="leave"
								bubbleClick={false}
							/>
						)
						: (
							<Button
								kind="primary"
								icon="plus"
								text={
									isChangingMembership
										? localize("_Joining") + "…"
										: localize("_Join")
								}
								isDisabled={isChangingMembership}
								onClick={onJoin}
								trackingId="join"
								bubbleClick={false}
							/>
						)
					}

				</div>
			</div>
		</div>
	);
};
CommunityCard.displayName = "CommunityCard";

type MoreOptionsProps = {
	trackingId: string;
	name: string;
	userInfo: CommunityUserInfo;
	onEdit: () => void;
	onDelete: () => void;
};

const MoreOptions = (props: MoreOptionsProps): ReactElement | null => {
	const {
		trackingId,
		name,
		userInfo,
		onEdit,
		onDelete
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const actions = useMemo(
		(): readonly CommunityAction[] => listManagementActions({
			onEdit,
			onDelete
		}),
		[onDelete, onEdit]
	);

	const actionContext: CommunityActionContext = userInfo;

	const availableActions = useMemo(
		(): readonly CommunityAction[] => reduceToAvailableActions(actions, actionContext),
		[actionContext, actions]
	);

	if (availableActions.length === 0) {
		return null;
	}

	return (
		<>
			<IconButton
				kind="transparent"
				icon="ellipsis"
				onClick={() => setIsOpen((x) => !x)}
				ref={ref}
				trackingId={trackingId}
				shape="round"
				isActive={isOpen}
				bubbleClick={false}
			/>

			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={() => setIsOpen(false)}
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={name}
					bubbleClick={false}
				>
					{availableActions.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, actionContext)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};
MoreOptions.displayName = "MoreOptions";

type CommunityActionContext = CommunityUserInfo;

type CommunityAction = Action<CommunityActionContext>;

type CommunityActionHandlers = {
	onEdit: () => void;
	onDelete: () => void;
};

function listManagementActions(
	handlers: CommunityActionHandlers
): readonly CommunityAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			icon: "pen",
			canExecute: (x): boolean => x.canEdit,
			execute: (): void => handlers.onEdit()
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (x): boolean => x.canDelete,
			execute: (): void => handlers.onDelete()
		}
	];
}
